import { Link } from "@/base/component/link";
import { Visible } from "@/base/component/visible";
import {
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Chip,
  Tooltip,
} from "@heroui/react";

export default function Feature() {
  return (
    <section className="py-16 bg-body-light dark:bg-body-dark" id="features">
      <div className="container mx-auto px-6">
        <div className="flex flex-wrap justify-center gap-4 max-w-5xl mx-auto">
          <FeatureTag
            tag="AI-powered semantic analysis"
            description="Leverage advanced language models to deeply understand your website content and identify hidden SEO opportunities for improved rankings"
          />
          <FeatureTag
            tag="Real-time data & live metrics"
            description="Access comprehensive analytics from PageSpeed Insights and DataForSEO for data-driven SEO decisions"
          />
          <FeatureTag
            tag="Personalized SEO strategies"
            description="Get customized optimization plans based on your website's unique characteristics and target audience"
          />
          <FeatureTag
            tag="Core web vitals optimization"
            description="Monitor and improve crucial performance metrics that directly impact your search engine rankings"
          />
          <FeatureTag
            tag="Actionable SEO insights"
            description="Receive prioritized, step-by-step recommendations to enhance your website's search visibility"
          />
          <FeatureTag
            tag="AI Chat assistant"
            description="Interactive AI chat that provides detailed answers about your website's SEO analysis and recommendations"
          />
          <FeatureTag
            tag="Content monitoring"
            description="Track industry trends by monitoring top websites for new content related to your keywords"
          />
        </div>
      </div>

      {/* Feature Highlights */}
      <div className="mt-24 flex flex-wrap justify-center gap-8 max-w-6xl mx-auto px-6">
        <FeatureCard
          title="Speed optimization"
          ariaLabel="Website performance optimization"
          description={
            <>
              Improve your search rankings with data from{" "}
              <Link href="https://pagespeed.web.dev/" target="_blank">
                PageSpeed Insights
              </Link>
              . Analyze loading times, user experience metrics, and core web
              vitals to identify speed issues holding back your SEO performance.
            </>
          }
          features={[
            "Mobile-first performance analysis",
            "Core web vitals tracking",
            "Loading speed optimization",
            "User experience metrics",
          ]}
        />
        <FeatureCard
          title="Content analysis"
          description={
            <>
              Get AI-powered analysis of your content structure, keywords, and
              semantic relevance. Examine through{" "}
              <Link href="https://dataforseo.com/" target="_blank">
                DataForSEO
              </Link>{" "}
              headings, meta descriptions, and content quality to help your
              pages match search intent and rank higher in results.
            </>
          }
          features={[
            "Mobile content readability",
            "Keyword density analysis",
            "Semantic relevance scoring",
            "Meta description optimization",
          ]}
          link="/seo-ai-pal-vs-competitors#how-it-works"
          ariaLabel="Learn more about SEO AI Pal's content analysis features"
        />
        <FeatureCard
          title="Content suggestions"
          ariaLabel="AI-powered content recommendations"
          description="Get smart content suggestions based on your target keywords and competitors. Analyzes top-performing content to help you create engaging, SEO-optimized articles."
          features={[
            "Topic clustering",
            "Content gap analysis",
            "Competitor content insights",
            "Keyword expansion ideas",
          ]}
        />
        <FeatureCard
          title="Content monitoring"
          ariaLabel="Real-time industry content tracking"
          description="Stay ahead of industry trends by monitoring selected websites for new content containing your tracked keywords. Get email alerts with article headlines as soon as they're published."
          features={[
            "Keyword tracking",
            "Website monitoring",
            "Email notifications",
            "Industry trend alerts",
          ]}
        />
        <FeatureCard
          title="AI Chat assistant"
          description="Ask to AI chat assistant specific questions about your SEO analysis. Get clarification on recommendations, explore insights about your website, and understand exactly what actions will improve your rankings."
          features={[
            "Mobile optimization tips",
            "Real-time SEO guidance",
            "Custom recommendations",
            "Technical SEO support",
          ]}
          link="/seo-ai-pal-vs-competitors#integration"
          ariaLabel="Learn more about SEO AI Pal's AI assistant features compared to competitors"
        />
      </div>
    </section>
  );
}

function FeatureTag({
  tag,
  description,
}: {
  tag: React.ReactNode;
  description: React.ReactNode;
}) {
  return (
    <Tooltip
      content={
        <p className="text-sm text-gray-600 dark:text-gray-300 max-w-sm truncate whitespace-normal text-center">
          {description}
        </p>
      }
    >
      <Chip
        size="lg"
        className="tracking-wide p-2 font-light text-sm whitespace-nowrap cursor-default"
        variant="dot"
        color="primary"
      >
        {tag}
      </Chip>
    </Tooltip>
  );
}

function FeatureCard({
  title,
  description,
  link,
  ariaLabel,
  features,
}: {
  title: React.ReactNode;
  description: React.ReactNode;
  link?: string;
  ariaLabel?: string;
  features?: string[];
}) {
  return (
    <Card className="p-2 max-w-xs">
      <CardHeader>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
          {title}
        </h2>
      </CardHeader>
      <CardBody>
        <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-4">
          {description}
        </p>
        {features && (
          <ul className="space-y-2">
            {features.map((feature, index) => (
              <li
                key={index}
                className="flex items-center text-sm text-gray-600 dark:text-gray-300"
              >
                <span className="text-green-500 mr-2">✓</span>
                {feature}
              </li>
            ))}
          </ul>
        )}
      </CardBody>
      <Visible on={!!link}>
        <CardFooter>
          <Link href={link} aria-label={ariaLabel} className="text-primary">
            Learn more
          </Link>
        </CardFooter>
      </Visible>
    </Card>
  );
}
